import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import InterpretationHoverPopup from './InterpretationHoverPopup';
import { NavLink, Link, useNavigate } from 'react-router-dom';

const ImageViewer = ({
  post,
  interpretations,
  currentAnnotation,
  hoveredInterpretation,
  imageRef,
  activeAnnotationTool,
  isDrawing,
  showAnnotations,
  onAnnotationStart,
  onAnnotationMove,
  onAnnotationEnd,
  onInterpretationHover,
  onDiscussInterpretation,
  onLikeInterpretation,
  onDislikeInterpretation,
  onViewFullInterpretation,
  user
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const [filteredUserId, setFilteredUserId] = useState(null);
  const [bannedUserIds, setBannedUserIds] = useState([]);
  const [imageDimensions, setImageDimensions] = useState(null);

  // Calculate actual displayed image dimensions using getBoundingClientRect
  // This logic works in tandem with the "Glassmorphism" layout where the img
  // is centered and sized with max-w-full max-h-full object-contain.
  const calculateDisplayedImageDimensions = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return null;
    
    // The container of the image (the wrapper div)
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // The image element itself (which now matches the visual bounds due to max-w/h + object-contain)
    const imageRect = imageRef.current.getBoundingClientRect();

    return {
      displayedWidth: imageRect.width,
      displayedHeight: imageRect.height,
      offsetX: imageRect.left - containerRect.left,
      offsetY: imageRect.top - containerRect.top,
      naturalWidth: imageRef.current.naturalWidth,
      naturalHeight: imageRef.current.naturalHeight,
      containerWidth: containerRect.width,
      containerHeight: containerRect.height,
      // Scale factor: how many displayed pixels per 1 natural pixel
      scaleX: imageRect.width / imageRef.current.naturalWidth,
      scaleY: imageRect.height / imageRef.current.naturalHeight
    };
  }, [imageRef]);

  // Update image dimensions on load and resize
  const updateImageDimensions = useCallback(() => {
    const dimensions = calculateDisplayedImageDimensions();
    if (dimensions) {
      setImageDimensions(dimensions);
    }
  }, [calculateDisplayedImageDimensions]);

  useEffect(() => {
    window.addEventListener('resize', updateImageDimensions);
    return () => window.removeEventListener('resize', updateImageDimensions);
  }, [updateImageDimensions]);

  const getCursorStyle = () => {
    if (activeAnnotationTool === 'box' || activeAnnotationTool === 'line') {
      return 'crosshair';
    }
    return 'grab';
  };

  // Get relative coordinates within the actual displayed image (not the container)
  const getRelativeCoordinates = useCallback((e) => {
    if (!imageRef.current || !containerRef.current || !imageDimensions) return { x: 0, y: 0 };
    
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Handle touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // 1. Get coordinates relative to the CONTAINER
    const containerRelativeX = clientX - containerRect.left;
    const containerRelativeY = clientY - containerRect.top;
    
    // 2. Subtract the offset (empty space around the image)
    const imageRelativeX = containerRelativeX - imageDimensions.offsetX;
    const imageRelativeY = containerRelativeY - imageDimensions.offsetY;
    
    // 3. Clamp to displayed image bounds
    const clampedX = Math.max(0, Math.min(imageRelativeX, imageDimensions.displayedWidth));
    const clampedY = Math.max(0, Math.min(imageRelativeY, imageDimensions.displayedHeight));
    
    return {
      x: clampedX,
      y: clampedY,
      containerX: containerRelativeX,
      containerY: containerRelativeY
    };
  }, [imageRef, imageDimensions]);

  const handleMouseDown = useCallback((e) => {
    if (activeAnnotationTool && user) {
      e.preventDefault();
      onAnnotationStart(e);
    }
  }, [activeAnnotationTool, user, onAnnotationStart]);

  const handleTouchStart = useCallback((e) => {
    if (activeAnnotationTool && user) {
      e.preventDefault();
      onAnnotationStart(e);
    }
  }, [activeAnnotationTool, user, onAnnotationStart]);

  const handleMouseMove = useCallback((e) => {
    if (isDrawing) {
      onAnnotationMove(e);
    }
  }, [isDrawing, onAnnotationMove]);

  const handleTouchMove = useCallback((e) => {
    if (isDrawing) {
      e.preventDefault();
      onAnnotationMove(e);
    }
  }, [isDrawing, onAnnotationMove]);

  const handleMouseUp = useCallback((e) => {
    if (isDrawing) {
      onAnnotationEnd(e);
    }
  }, [isDrawing, onAnnotationEnd]);

  const handleTouchEnd = useCallback((e) => {
    if (isDrawing) {
      e.preventDefault();
      onAnnotationEnd(e);
    }
  }, [isDrawing, onAnnotationEnd]);

  const handleAnnotationHover = (interpretation, event) => {
    if (interpretation && event && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      
      setPopupPosition({
        x: clientX - containerRect.left,
        y: clientY - containerRect.top
      });
    }
    onInterpretationHover(interpretation);
  };

  const handleAnnotationLeave = () => {
    setTimeout(() => {
      if (!isPopupHovered) {
        onInterpretationHover(null);
      }
    }, 100);
  };

  const handlePopupMouseEnter = () => setIsPopupHovered(true);
  const handlePopupMouseLeave = () => {
    setIsPopupHovered(false);
    onInterpretationHover(null);
  };

  const handleViewProfile = (userId) => {
    navigate(`/profile/user/${userId}`);
  };

  const handleFilterByUser = (userId) => {
    setFilteredUserId(filteredUserId === userId ? null : userId);
  };

  const handleBanUser = (userId) => {
    setBannedUserIds((prev) =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const renderAnnotation = (interpretation) => {
    const { shape_type, shape_data } = interpretation;
    
    if (!imageDimensions) return null;

    const {
      displayedWidth,
      displayedHeight,
      offsetX,
      offsetY,
      naturalWidth,
      naturalHeight,
      scaleX,
      scaleY
    } = imageDimensions;

    // NOTE: normalized coordinates (0-1) in shape_data represent
    // position relative to the IMAGE, not the container.
    // So we multiply by naturalWidth/Height and then by scale (to get displayed pixels),
    // and finally add the offset (to position it correctly in the container).

    if (shape_type === 'BOX') {
      const left = shape_data.x * naturalWidth * scaleX + offsetX;
      const top = shape_data.y * naturalHeight * scaleY + offsetY;
      const width = shape_data.width * naturalWidth * scaleX;
      const height = shape_data.height * naturalHeight * scaleY;

      // Ensure the annotation stays within the displayed image bounds
      // (Though theoretically valid data should always be within bounds)
      const clampedLeft = Math.max(offsetX, Math.min(left, offsetX + displayedWidth));
      const clampedTop = Math.max(offsetY, Math.min(top, offsetY + displayedHeight));
      
      // Calculate max available width/height from this start point
      const maxAvailableWidth = (offsetX + displayedWidth) - clampedLeft;
      const maxAvailableHeight = (offsetY + displayedHeight) - clampedTop;
      
      const clampedWidth = Math.min(width, maxAvailableWidth);
      const clampedHeight = Math.min(height, maxAvailableHeight);

      return (
        <div
          key={interpretation.id}
          className={`absolute cursor-pointer transition-all duration-200 z-20 ${
            hoveredInterpretation?.id === interpretation.id
              ? 'border-blue-400 bg-blue-400/20 border-2 sm:border-4'
              : 'border-yellow-400 bg-yellow-400/10 border-1 sm:border-2'
          }`}
          style={{
            left: clampedLeft,
            top: clampedTop,
            width: Math.max(clampedWidth, 10),
            height: Math.max(clampedHeight, 10),
          }}
          onMouseEnter={(e) => handleAnnotationHover(interpretation, e)}
          onMouseLeave={handleAnnotationLeave}
          onTouchStart={(e) => {
            e.preventDefault();
            handleAnnotationHover(interpretation, e);
          }}
        />
      );
    } else if (shape_type === 'LINE') {
      const startX = shape_data.startX * naturalWidth * scaleX + offsetX;
      const startY = shape_data.startY * naturalHeight * scaleY + offsetY;
      const endX = shape_data.endX * naturalWidth * scaleX + offsetX;
      const endY = shape_data.endY * naturalHeight * scaleY + offsetY;

      // Clamp line endpoints to displayed image bounds
      const clampX = (val) => Math.max(offsetX, Math.min(val, offsetX + displayedWidth));
      const clampY = (val) => Math.max(offsetY, Math.min(val, offsetY + displayedHeight));

      const cStartX = clampX(startX);
      const cStartY = clampY(startY);
      const cEndX = clampX(endX);
      const cEndY = clampY(endY);

      const length = Math.sqrt((cEndX - cStartX) ** 2 + (cEndY - cStartY) ** 2);
      const angle = Math.atan2(cEndY - cStartY, cEndX - cStartX) * (180 / Math.PI);

      return (
        <div
          key={interpretation.id}
          className={`absolute cursor-pointer transition-all duration-200 z-20 ${
            hoveredInterpretation?.id === interpretation.id
              ? 'border-blue-400 border-t-2 sm:border-t-4'
              : 'border-yellow-400 border-t-1 sm:border-t-2'
          }`}
          style={{
            left: cStartX,
            top: cStartY,
            width: Math.max(length, 10),
            transformOrigin: 'left center',
            transform: `rotate(${angle}deg)`,
          }}
          onMouseEnter={(e) => handleAnnotationHover(interpretation, e)}
          onMouseLeave={handleAnnotationLeave}
          onTouchStart={(e) => {
            e.preventDefault();
            handleAnnotationHover(interpretation, e);
          }}
        />
      );
    }
    return null;
  };

  const renderCurrentAnnotation = () => {
    if (!currentAnnotation || !imageDimensions) return null;

    const { offsetX, offsetY } = imageDimensions;

    if (currentAnnotation.type === 'box') {
      const x = Math.min(currentAnnotation.startX, currentAnnotation.endX);
      const y = Math.min(currentAnnotation.startY, currentAnnotation.endY);
      const width = Math.abs(currentAnnotation.endX - currentAnnotation.startX);
      const height = Math.abs(currentAnnotation.endY - currentAnnotation.startY);

      return (
        <div
          className="absolute border-2 border-blue-500 bg-blue-500/20 pointer-events-none z-20"
          style={{ 
            left: x + offsetX, 
            top: y + offsetY, 
            width: Math.max(0, width), 
            height: Math.max(0, height)
          }}
        />
      );
    } else if (currentAnnotation.type === 'line') {
      const length = Math.sqrt(
        Math.pow(currentAnnotation.endX - currentAnnotation.startX, 2) +
        Math.pow(currentAnnotation.endY - currentAnnotation.startY, 2)
      );
      const angle = Math.atan2(
        currentAnnotation.endY - currentAnnotation.startY,
        currentAnnotation.endX - currentAnnotation.startX
      ) * 180 / Math.PI;

      return (
        <div
          className="absolute border-t-2 border-blue-500 pointer-events-none z-20"
          style={{
            left: currentAnnotation.startX + offsetX,
            top: currentAnnotation.startY + offsetY,
            width: Math.max(length, 0),
            transformOrigin: 'left center',
            transform: `rotate(${angle}deg)`,
          }}
        />
      );
    }
    return null;
  };

  return (
    <div ref={containerRef} className="relative bg-gray-900 rounded-lg overflow-hidden group">
      {/* 
        Container Logic:
        - Fixed responsive height creates the "stage".
        - Flexbox centers the content.
        - Mouse events are captured here to allow dragging from outside the image.
      */}
      <div
        className="relative overflow-hidden w-full h-[50vh] sm:h-[65vh] lg:h-[80vh] flex justify-center items-center bg-black"
        style={{ cursor: getCursorStyle() }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Aesthetic Blurred Background */}
        <div className="absolute inset-0 z-0 overflow-hidden select-none pointer-events-none bg-black">
          <img
            src={`https://app.writeapp.in${post.image_url}`}
            alt=""
            className="w-full h-full object-cover blur-3xl scale-125 opacity-60"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Main Image Container */}
        {/* 
            - max-w/h-full + object-contain ensures the image fits WITHOUT being cropped.
            - It "shrinks" to fit the aspect ratio.
            - z-10 puts it above the background.
        */}
        <div className="relative z-10 flex justify-center items-center w-full h-full p-4 pointer-events-none">
           <img
            ref={imageRef}
            src={`https://app.writeapp.in${post.image_url}`}
            alt={post.title}
            className="max-w-full max-h-full object-contain shadow-2xl drop-shadow-2xl rounded-sm pointer-events-auto"
            style={{
              cursor: getCursorStyle()
            }}
            onLoad={(e) => {
              const img = e.target;
              setImageLoaded(true);
              // Trigger dimension calculation after image loads
              setTimeout(() => {
                updateImageDimensions();
              }, 100);
            }}
            draggable={false}
            onError={() => {
              console.error('Failed to load image');
            }}
          />
        </div>

        {/* Annotations Layer */}
        {/* 
            Annotations are absolutely positioned relative to the container.
            Their position includes the calculated `offsetX` and `offsetY`
            so they align perfectly over the image.
        */}
        {imageLoaded && showAnnotations && imageDimensions &&
          (
            filteredUserId
              ? interpretations.filter(i => i.user?.id === filteredUserId)
              : interpretations
          ).filter(i => !bannedUserIds.includes(i.user?.id)).map(renderAnnotation)}

        {imageLoaded && renderCurrentAnnotation()}
      </div>

      <AnimatePresence>
        {hoveredInterpretation && (
          <InterpretationHoverPopup
            interpretation={hoveredInterpretation}
            position={popupPosition}
            containerRef={containerRef}
            onDiscuss={onDiscussInterpretation}
            onLike={onLikeInterpretation}
            onDislike={onDislikeInterpretation}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
            onViewProfile={handleViewProfile}
            onViewFullInterpretation={onViewFullInterpretation}
            onFilterByUser={handleFilterByUser}
            filteredUserId={filteredUserId}
            onBanUser={handleBanUser}
            bannedUserIds={bannedUserIds}
            user={user}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageViewer;